import { Box, Typography } from '@mui/material'

import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { useMemo } from 'react'

export const getEngineeringUiStyles = ({ themeMode, textColor, accentColor, cardBackground }) => {
  const isDark = themeMode === 'dark'

  const surfaceBackground = isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)'
  const surfaceBorder = isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(0,0,0,0.10)'

  const rowBackground = isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)'
  const rowHoverBackground = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'

  const dividerTrack = 'rgba(77,163,255,0.15)'
  const fieldBackground = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'

  return {
    defaultTextColor: textColor,
    page: {
      backgroundColor: cardBackground,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minHeight: 0,
    },

    glassCard: {
      borderRadius: 2,
      background: surfaceBackground,
      border: surfaceBorder,
      backdropFilter: 'blur(10px)',
    },

    glassFrame: {
      borderRadius: 2,
      background: 'transparent',
      border: surfaceBorder,
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      display: 'flex',
    },

    glassCardHeader: {
      p: 1,
      borderBottom: surfaceBorder,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },

    surface: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      minWidth: 0,
      p: 2,
      borderRadius: 2,
      background: surfaceBackground,
      border: surfaceBorder,
      backdropFilter: 'blur(10px)',
      width: '100%',
      flex: 1,
    },

    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      minWidth: 0,
      p: 1.5,
      borderRadius: 2,
      background: fieldBackground,
      border: surfaceBorder,
    },

    titleWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      mb: 1.25,
    },

    titleText: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: textColor,
      opacity: 0.9,
    },

    titleDividerTrack: {
      position: 'relative',
      height: 2,
      width: '100%',
      borderRadius: 2,
      background: dividerTrack,
      overflow: 'hidden',
    },

    titleDividerGlow: {
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: 180,
      background: 'linear-gradient(90deg, #4da3ff 0%, rgba(77,163,255,0.6) 60%, transparent 100%)',
    },

    row: {
      display: 'grid',
      gridTemplateColumns: '160px minmax(0, max-content) 90px',
      alignItems: 'center',
      columnGap: 2,
      px: 1.5,
      py: 1,
      borderRadius: 1.5,
      transition: 'background 0.15s ease',
      backgroundColor: rowBackground,
      '&:hover': { backgroundColor: rowHoverBackground },
    },

    label: {
      fontSize: 12,
      fontWeight: 500,
      color: textColor,
      opacity: 0.9,
      whiteSpace: 'nowrap',
    },

    units: {
      fontSize: 11,
      color: textColor,
      opacity: 0.6,
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },

    input: {
      width: '100%',
      minWidth: 0,
      '& .MuiInputBase-root': {
        borderRadius: 1.5,
        fontSize: 12,
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      },
      '& .MuiInputBase-input': {
        py: 0.9,
        color: textColor,
      },
    },

    inputDisabled: {
      width: '100%',
      minWidth: 0,
      '& .MuiInputBase-root': {
        borderRadius: 1.5,
        fontSize: 12,
        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      },
      '& .MuiInputBase-input': {
        py: 0.9,
        color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      },
    },

    checkboxCell: {
      display: 'flex',
      alignItems: 'center',
      height: 32,
    },

    iconButton: {
      p: 0.5,
      borderRadius: 1.25,
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      border: surfaceBorder,
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
      },
    },

    iconButtonAdd: {
      p: 0.5,
      borderRadius: 1.25,
      backgroundColor: isDark ? 'rgba(60,200,120,0.15)' : 'rgba(60,200,120,0.12)',
      border: '1px solid rgba(60,200,120,0.35)',
      color: '#3cc878',

      '&:hover': {
        backgroundColor: isDark ? 'rgba(60,200,120,0.25)' : 'rgba(60,200,120,0.18)',
      },
    },

    iconButtonDelete: {
      p: 0.5,
      borderRadius: 1.25,
      backgroundColor: isDark ? 'rgba(220,80,80,0.15)' : 'rgba(220,80,80,0.12)',
      border: '1px solid rgba(220,80,80,0.35)',
      color: '#e25b5b',

      '&:hover': {
        backgroundColor: isDark ? 'rgba(220,80,80,0.25)' : 'rgba(220,80,80,0.18)',
      },
    },

    iconButtonExtra: {
      p: 0.5,
      borderRadius: 1.25,
      backgroundColor: isDark ? 'rgba(255,255,100,0.15)' : 'rgba(180,130,0,0.10)',
      border: isDark ? '1px solid rgba(255,255,100,0.35)' : '1px solid rgba(180,130,0,0.40)',
      color: isDark ? '#ffff64' : '#8a6800',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,100,0.25)' : 'rgba(180,130,0,0.18)',
      },
    },

    tabBar: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
    },

    tabRail: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      padding: '4px 6px',
      borderRadius: 6,
      backdropFilter: 'blur(6px)',
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      width: 'fit-content',
      maxWidth: '100%',
    },

    tab: {
      selectedBackgroundColor: isDark ? '#2c3032' : '#4c85c2',
      defaultBackgroundColor: isDark ? '#222628' : '#f5f5f5',
      selectedTextColor: isDark ? '#ffffff' : '#ffffff',
      defaultTextColor: isDark ? '#ffffff' : '#000000',
    },
  }
}

export const useEngineeringUi = () => {
  const { theme, getTextColor, getAccentColor, getCardBackground } = useInnovaTheme()

  return useMemo(() => {
    return getEngineeringUiStyles({
      themeMode: theme,
      textColor: getTextColor(),
      accentColor: getAccentColor(),
      cardBackground: getCardBackground(),
    })
  }, [theme, getTextColor, getAccentColor, getCardBackground])
}

export const GlassTitle = ({ title, ui, sx = null, icon = null, lockIcon = null, trailingIcon = null }) => {
  if (!title) return null

  return (
    <Box sx={{ ...ui?.titleWrap, ...(sx || {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: 0.85 }}>
          {icon ? <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box> : null}
          <Typography sx={ui?.titleText}>{title}</Typography>
          {lockIcon ? <Box sx={{ display: 'flex', alignItems: 'center' }}>{lockIcon}</Box> : null}
        </Box>

        {trailingIcon ? <Box sx={{ display: 'flex', alignItems: 'center' }}>{trailingIcon}</Box> : null}
      </Box>

      <Box sx={ui?.titleDividerTrack}>
        <Box sx={ui?.titleDividerGlow} />
      </Box>
    </Box>
  )
}

export const GlassField = ({ title, ui, sx = null, children }) => {
  return (
    <Box sx={{ ...ui?.field, ...(sx || {}) }}>
      <GlassTitle title={title} ui={ui} />
      {children}
    </Box>
  )
}
